import { InfoIcon } from 'icons'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styles from './InfoPopover.module.scss'

const POPOVER_WIDTH = 323
const POPOVER_OFFSET = 8

/**
 * Info icon that opens a small popover with a title, a message and a call to action.
 * The popover closes on outside click.
 *
 * @param {string} title - Popover heading.
 * @param {string} message - Popover body text.
 * @param {Function} linkAction - Runs when the call to action is clicked.
 * @param {string} linkText - Text of the call to action.
 * @param {string} [linkType] - Call to action style. Pass 'button' to show it as
 *   a button below the message. By default it is an inline link inside the message.
 * @param {string} [placement] - Where the popover opens relative to the icon.
 *   Pass 'bottom' to open below the icon. Defaults to 'top' (above the icon).
 */
export const InfoPopover = ({
  title,
  message,
  linkAction,
  linkText,
  linkType,
  placement = 'top'
}) => {
  const [showMessage, setShowMessage] = useState(false)
  const [position, setPosition] = useState(null)
  const triggerRef = useRef(null)
  const popoverRef = useRef(null)

  const handleClick = () => {
    setShowMessage(prev => !prev)
  }

  useEffect(() => {
    if (!showMessage || !triggerRef.current) return

    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect()
      setPosition(
        placement === 'bottom'
          ? {
              // Anchor the popover's top-left corner next to the icon so it opens
              // to the right and downwards.
              top: rect.bottom + POPOVER_OFFSET,
              left: rect.left
            }
          : {
              bottom: window.innerHeight - rect.top + POPOVER_OFFSET,
              left: rect.left + rect.width / 2
            }
      )
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [showMessage, placement])

  useEffect(() => {
    if (!showMessage) return

    const handleClickOutside = event => {
      const insideTrigger = triggerRef.current && triggerRef.current.contains(event.target)
      const insidePopover = popoverRef.current && popoverRef.current.contains(event.target)
      if (!insideTrigger && !insidePopover) {
        setShowMessage(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showMessage])

  return (
    <div className={styles.infoPopoverContainer}>
      <div
        ref={triggerRef}
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      >
        <img
          src={InfoIcon}
          alt='Info'
          style={{ width: '16px', height: '16px' }}
        />
      </div>
      {showMessage &&
        position &&
        createPortal(
          <div
            ref={popoverRef}
            className={styles.infoMessage}
            style={{
              ...(placement === 'bottom'
                ? { top: `${position.top}px`, transform: 'none' }
                : { bottom: `${position.bottom}px` }),
              left: `${position.left}px`,
              width: `${POPOVER_WIDTH}px`
            }}
          >
            <span className={styles.messageTitle}>{title}</span>
            {linkType === 'button' ? (
              <>
                <p className={styles.messageText}>{message}</p>
                <button
                  type='button'
                  className={styles.messageButton}
                  onClick={linkAction}
                >
                  {linkText}
                </button>
              </>
            ) : (
              <p className={styles.messageText}>
                {message}
                <a onClick={linkAction}>{linkText}</a>
              </p>
            )}
          </div>,
          document.body
        )}
    </div>
  )
}
